import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabaseClient';
import useUsageLimits from '../hooks/useUsageLimits';
import UpgradeModal from '../components/UpgradeModal';
import AuthModal from '../components/AuthModal';
import { 
  Plus, Camera, Utensils, Trash2, Calendar, Flame, Beef, Wheat, 
  Droplets, Sparkles, ChevronLeft, ChevronRight, X, Loader, 
  Coffee, Sun, Moon, Apple, Lock
} from 'lucide-react';

// API sécurisée via Supabase Edge Function
const SUPABASE_URL = 'https://ififowdcasczwlydunfj.supabase.co';
const OPENAI_PROXY = `${SUPABASE_URL}/functions/v1/openai-proxy`;

const JournalPage = () => {
  const { user, profile, isAuthenticated } = useAuth();
  const { canUse, getLimit, incrementUsage, isPro } = useUsageLimits();
  
  const [entries, setEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [weeklyStats, setWeeklyStats] = useState([]);
  const [aiAdvice, setAiAdvice] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Limites
  const journalLimit = getLimit('journal');
  const canAdd = canUse('journal');

  const loadEntries = useCallback(async () => {
    if (!user) return;
    setIsLoading(true);
    try {
      const startOfDay = new Date(selectedDate);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(selectedDate);
      endOfDay.setHours(23, 59, 59, 999);

      const { data } = await supabase
        .from('food_journal')
        .select('*')
        .eq('user_id', user.id)
        .gte('created_at', startOfDay.toISOString())
        .lte('created_at', endOfDay.toISOString())
        .order('created_at', { ascending: true });

      setEntries(data || []);
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setIsLoading(false);
    }
  }, [user, selectedDate]);

  const loadWeeklyStats = useCallback(async () => {
    if (!user) return;
    try {
      const today = new Date();
      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 6);
      weekAgo.setHours(0, 0, 0, 0);

      const { data } = await supabase
        .from('food_journal')
        .select('created_at, calories, protein, carbs, fat')
        .eq('user_id', user.id)
        .gte('created_at', weekAgo.toISOString());

      const dailyStats = {};
      for (let i = 6; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        const key = date.toISOString().split('T')[0];
        dailyStats[key] = { date: key, calories: 0, protein: 0, carbs: 0, fat: 0 };
      }

      (data || []).forEach(entry => {
        const key = entry.created_at.split('T')[0];
        if (dailyStats[key]) {
          dailyStats[key].calories += entry.calories || 0;
        }
      });

      setWeeklyStats(Object.values(dailyStats));
    } catch (err) {
      console.error('Erreur:', err);
    }
  }, [user, isPro]);

  useEffect(() => {
    if (isPro) {
      loadEntries();
      loadWeeklyStats();
    } else {
      setIsLoading(false);
    }
  }, [loadEntries, loadWeeklyStats, isPro]);

  const todayTotals = entries.reduce((acc, entry) => ({
    calories: acc.calories + (entry.calories || 0),
    protein: acc.protein + (entry.protein || 0),
    carbs: acc.carbs + (entry.carbs || 0),
    fat: acc.fat + (entry.fat || 0)
  }), { calories: 0, protein: 0, carbs: 0, fat: 0 });

  const goals = {
    calories: profile?.daily_calories || 2000,
    protein: Math.round((profile?.daily_calories || 2000) * 0.25 / 4),
    carbs: Math.round((profile?.daily_calories || 2000) * 0.45 / 4),
    fat: Math.round((profile?.daily_calories || 2000) * 0.30 / 9)
  };

  const changeDate = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + direction);
    setSelectedDate(newDate);
  };

  const isToday = selectedDate.toDateString() === new Date().toDateString();

  const deleteEntry = async (id) => {
    await supabase.from('food_journal').delete().eq('id', id);
    loadEntries();
    loadWeeklyStats();
  };

  const generateAdvice = async () => {
    if (entries.length === 0) return;
    setIsAnalyzing(true);
    try {
      const response = await fetch(OPENAI_PROXY, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [
            { role: 'system', content: `Tu es Louis, coach nutrition. Donne un conseil court (2-3 phrases) et encourageant. Objectif: ${goals.calories} kcal/jour.` },
            { role: 'user', content: `Repas: ${entries.map(e => e.description).join(', ')}. Total: ${todayTotals.calories} kcal.` }
          ],
          max_tokens: 100
        })
      });
      const data = await response.json();
      setAiAdvice(data.choices[0].message.content);
    } catch (err) {
      setAiAdvice('Continue comme ça ! Chaque repas compte. 💪');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const formatDate = (date) => date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });

  const getMealIcon = (type) => {
    const icons = { breakfast: <Coffee size={18} />, lunch: <Sun size={18} />, dinner: <Moon size={18} />, snack: <Apple size={18} /> };
    return icons[type] || <Utensils size={18} />;
  };

  const getMealLabel = (type) => {
    const labels = { breakfast: 'Petit-déjeuner', lunch: 'Déjeuner', dinner: 'Dîner', snack: 'Collation' };
    return labels[type] || 'Repas';
  };

  const maxCalories = Math.max(...weeklyStats.map(s => s.calories), goals.calories, 1);

  // Fonction pour ouvrir le modal d'ajout avec vérification
  const handleAddClick = () => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }
    if (!canAdd) {
      setShowUpgradeModal(true);
      return;
    }
    setShowAddModal(true);
  };

  // onSave avec incrémentation de l'usage
  const handleSaveEntry = () => {
    incrementUsage('journal');
    loadEntries();
    loadWeeklyStats();
    setShowAddModal(false);
  };

  // Si non connecté, afficher invitation à s'inscrire
  if (!isAuthenticated) {
    return (
      <div className="journal-page">
        <div className="locked-feature-card">
          <div className="locked-emoji">📓</div>
          <h1>Journal Alimentaire</h1>
          <p>Inscrivez-vous gratuitement pour tracker vos repas et suivre vos calories automatiquement !</p>
          <div className="locked-benefits">
            <div className="benefit-highlight">🎁 <strong>5 repas analysés par mois OFFERTS</strong></div>
            <div>✓ Calcul automatique de vos calories</div>
            <div>✓ Suivi de vos macros (protéines, glucides, lipides)</div>
            <div>✓ Graphiques de progression sur 7 jours</div>
          </div>
          <button className="primary-btn" onClick={() => setShowAuthModal(true)}>
            <Sparkles size={20} />
            Créer mon compte gratuit
          </button>
          <p className="locked-subtext">Inscription en 30 secondes • Sans engagement • Sans carte bancaire</p>
        </div>
        {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}
      </div>
    );
  }

  return (
    <div className="journal-page">
      <div className="page-header">
        <div>
          <h1>📓 Journal Alimentaire</h1>
          <p>Suivez votre alimentation et atteignez vos objectifs</p>
          {!isPro && (
            <span className="usage-badge">
              {journalLimit.isUnlimited ? '∞' : journalLimit.remaining} repas restants ce mois
              {journalLimit.remaining <= 2 && !journalLimit.isUnlimited && <Lock size={14} />}
            </span>
          )}
        </div>
        <button className="primary-btn" onClick={handleAddClick} disabled={!canAdd && !isPro}>
          <Plus size={20} />
          <span>Ajouter un repas</span>
        </button>
      </div>

      <div className="date-nav">
        <button onClick={() => changeDate(-1)} className="date-nav-btn"><ChevronLeft size={20} /></button>
        <div className="current-date">
          <Calendar size={18} />
          <span>{isToday ? "Aujourd'hui" : formatDate(selectedDate)}</span>
        </div>
        <button onClick={() => changeDate(1)} className="date-nav-btn" disabled={isToday}><ChevronRight size={20} /></button>
        </div>

        <div className="stats-grid-4">
          <div className="stat-card orange">
            <Flame size={24} />
            <div className="stat-content">
              <span className="stat-value">{todayTotals.calories}</span>
              <span className="stat-label">/ {goals.calories} kcal</span>
            </div>
            <div className="stat-bar"><div style={{ width: `${Math.min(100, (todayTotals.calories / goals.calories) * 100)}%` }} /></div>
          </div>
          <div className="stat-card red">
            <Beef size={24} />
            <div className="stat-content">
              <span className="stat-value">{todayTotals.protein}g</span>
              <span className="stat-label">/ {goals.protein}g prot.</span>
            </div>
            <div className="stat-bar"><div style={{ width: `${Math.min(100, (todayTotals.protein / goals.protein) * 100)}%` }} /></div>
          </div>
          <div className="stat-card blue">
            <Wheat size={24} />
            <div className="stat-content">
              <span className="stat-value">{todayTotals.carbs}g</span>
              <span className="stat-label">/ {goals.carbs}g gluc.</span>
            </div>
            <div className="stat-bar"><div style={{ width: `${Math.min(100, (todayTotals.carbs / goals.carbs) * 100)}%` }} /></div>
          </div>
          <div className="stat-card purple">
            <Droplets size={24} />
            <div className="stat-content">
              <span className="stat-value">{todayTotals.fat}g</span>
              <span className="stat-label">/ {goals.fat}g lip.</span>
            </div>
            <div className="stat-bar"><div style={{ width: `${Math.min(100, (todayTotals.fat / goals.fat) * 100)}%` }} /></div>
          </div>
        </div>

        <div className="card">
          <h2>🍽️ Repas du jour</h2>
          {isLoading ? (
            <div className="loading-state"><Loader size={24} className="spin" /><span>Chargement...</span></div>
          ) : entries.length === 0 ? (
            <div className="empty-state">
              <Utensils size={48} />
              <h3>Aucun repas enregistré</h3>
              <p>Ajoutez votre premier repas pour commencer !</p>
              <button onClick={handleAddClick} className="primary-btn"><Plus size={18} />Mon premier repas</button>
            </div>
          ) : (
            <div className="meals-list">
              {entries.map(entry => (
                <div key={entry.id} className="meal-item">
                  <div className="meal-icon">{getMealIcon(entry.meal_type)}</div>
                  <div className="meal-info">
                    <div className="meal-header">
                      <span className="meal-type">{getMealLabel(entry.meal_type)}</span>
                      <span className="meal-time">{new Date(entry.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                    <p className="meal-desc">{entry.description}</p>
                    <div className="meal-macros">
                      <span><Flame size={14} /> {entry.calories}</span>
                      <span><Beef size={14} /> {entry.protein}g</span>
                      <span><Wheat size={14} /> {entry.carbs}g</span>
                      <span><Droplets size={14} /> {entry.fat}g</span>
                    </div>
                  </div>
                  <button className="delete-btn" onClick={() => deleteEntry(entry.id)}><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card">
          <h2>📊 Évolution sur 7 jours</h2>
          <div className="week-chart">
            {weeklyStats.map((day, i) => (
              <div key={i} className="chart-day">
                <div className="chart-bar-wrap">
                  <div className={`chart-bar ${day.calories >= goals.calories ? 'over' : ''}`} style={{ height: `${Math.max(8, (day.calories / maxCalories) * 100)}%` }}>
                    {day.calories > 0 && <span>{day.calories}</span>}
                  </div>
                </div>
                <span className="chart-label">{new Date(day.date).toLocaleDateString('fr-FR', { weekday: 'short' })}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="advice-card">
          <div className="advice-header">
            <h2><Sparkles size={20} /> Conseil de Louis</h2>
            {entries.length > 0 && (
              <button onClick={generateAdvice} disabled={isAnalyzing} className="secondary-btn small">
                {isAnalyzing ? <Loader size={16} className="spin" /> : <Sparkles size={16} />}
                {isAnalyzing ? 'Analyse...' : 'Analyser'}
              </button>
            )}
          </div>
          {aiAdvice ? (
            <div className="advice-content">
              <img src="./robot-icon.svg" alt="Louis" className="advice-avatar" />
              <p>{aiAdvice}</p>
            </div>
          ) : (
            <p className="advice-placeholder">Ajoutez vos repas puis cliquez sur "Analyser" pour un conseil personnalisé !</p>
          )}
        </div>

        {showAddModal && (
          <AddMealModal 
            onClose={() => setShowAddModal(false)}
            onSave={handleSaveEntry}
            userId={user?.id}
            selectedDate={selectedDate}
          />
        )}

        {showUpgradeModal && <UpgradeModal onClose={() => setShowUpgradeModal(false)} />}
        {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}
      </div>
  );
};

const AddMealModal = ({ onClose, onSave, userId, selectedDate }) => {
  const [mealType, setMealType] = useState('lunch');
  const [description, setDescription] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [nutritionData, setNutritionData] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [mode, setMode] = useState('text');
  const [photoPreview, setPhotoPreview] = useState(null);

  const mealTypes = [
    { id: 'breakfast', label: 'Petit-déj', icon: <Coffee size={20} /> },
    { id: 'lunch', label: 'Déjeuner', icon: <Sun size={20} /> },
    { id: 'dinner', label: 'Dîner', icon: <Moon size={20} /> },
    { id: 'snack', label: 'Collation', icon: <Apple size={20} /> }
  ];

  const analyzeFood = async () => {
    if (!description.trim() && !photoPreview) return;
    setIsAnalyzing(true);
    setNutritionData(null);
    
    try {
      const messages = [
        {
          role: 'system',
          content: `Tu es une base de données nutritionnelle précise. Donne les valeurs EXACTES pour l'aliment demandé.

RÉFÉRENCES (à respecter strictement):
- Pomme (150g): 78 kcal, 0g prot, 21g gluc, 0g lip
- Banane (120g): 105 kcal, 1g prot, 27g gluc, 0g lip
- Orange (150g): 62 kcal, 1g prot, 15g gluc, 0g lip
- Oeuf (60g): 90 kcal, 8g prot, 1g gluc, 6g lip
- Pain blanc (30g): 80 kcal, 2g prot, 15g gluc, 1g lip
- Yaourt nature (125g): 70 kcal, 5g prot, 6g gluc, 3g lip
- Poulet grillé (100g): 165 kcal, 31g prot, 0g gluc, 4g lip
- Riz cuit (150g): 195 kcal, 4g prot, 42g gluc, 0g lip
- Pâtes cuites (150g): 220 kcal, 8g prot, 43g gluc, 1g lip
- Salade verte (100g): 15 kcal, 1g prot, 2g gluc, 0g lip
- Salade César: 350 kcal, 20g prot, 12g gluc, 25g lip
- Steak haché (100g): 250 kcal, 26g prot, 0g gluc, 17g lip
- Saumon (100g): 208 kcal, 20g prot, 0g gluc, 13g lip

Réponds UNIQUEMENT avec ce JSON exact:
{"calories": nombre, "protein": nombre, "carbs": nombre, "fat": nombre}`
        },
        {
          role: 'user',
          content: `Donne les valeurs nutritionnelles exactes pour: ${description}`
        }
      ];

      // Appel via le proxy sécurisé Supabase
      const response = await fetch(OPENAI_PROXY, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages, model: 'gpt-4o-mini', max_tokens: 100, temperature: 0.1 })
      });

      const data = await response.json();
      if (data.choices?.[0]) {
        const content = data.choices[0].message.content;
        const jsonMatch = content.match(/\{[\s\S]*?\}/);
        if (jsonMatch) {
          const parsed = JSON.parse(jsonMatch[0]);
          setNutritionData(parsed);
        }
      }
    } catch (err) {
      console.error('Erreur:', err);
      setNutritionData({ calories: 200, protein: 8, carbs: 25, fat: 8 });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => { setPhotoPreview(reader.result); setNutritionData(null); };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    if (!nutritionData || !description.trim()) return;
    setIsSaving(true);
    try {
      const entryDate = new Date(selectedDate);
      const now = new Date();
      entryDate.setHours(now.getHours(), now.getMinutes());

      await supabase.from('food_journal').insert({
        user_id: userId,
        meal_type: mealType,
        description: description.trim(),
        calories: nutritionData.calories,
        protein: nutritionData.protein,
        carbs: nutritionData.carbs,
        fat: nutritionData.fat,
        created_at: entryDate.toISOString()
      });
      onSave();
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>🍽️ Ajouter un repas</h2>
          <button onClick={onClose} className="modal-close-btn"><X size={24} /></button>
        </div>

        <div className="modal-body">
          <div className="meal-type-grid">
            {mealTypes.map(type => (
              <button key={type.id} className={`meal-type-btn ${mealType === type.id ? 'active' : ''}`} onClick={() => setMealType(type.id)}>
                {type.icon}
                <span>{type.label}</span>
              </button>
            ))}
          </div>

          <div className="input-mode-tabs">
            <button className={`tab-btn ${mode === 'text' ? 'active' : ''}`} onClick={() => setMode('text')}>
              <Utensils size={18} /> Décrire
            </button>
            <button className={`tab-btn ${mode === 'photo' ? 'active' : ''}`} onClick={() => setMode('photo')}>
              <Camera size={18} /> Photo
            </button>
          </div>

          {mode === 'photo' ? (
            <div className="photo-section">
              {photoPreview ? (
                <div className="photo-preview-box">
                  <img src={photoPreview} alt="Repas" />
                  <button onClick={() => setPhotoPreview(null)} className="photo-remove-btn"><X size={16} /></button>
                </div>
              ) : (
                <label className="photo-upload-box">
                  <Camera size={32} />
                  <span>Prendre une photo</span>
                  <input type="file" accept="image/*" capture="environment" onChange={handlePhotoUpload} />
                </label>
              )}
              <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Décris ce que tu vois..." rows={2} />
            </div>
          ) : (
            <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Ex: Une pomme, Salade César, Pâtes bolognaise..." rows={3} className="description-input" />
          )}

          {!nutritionData && (
            <button onClick={analyzeFood} disabled={isAnalyzing || (!description.trim() && !photoPreview)} className="analyze-btn">
              {isAnalyzing ? <><Loader size={18} className="spin" /> Analyse en cours...</> : <><Sparkles size={18} /> Calculer les calories</>}
            </button>
          )}

          {nutritionData && (
            <div className="nutrition-result">
              <h3>✅ Valeurs nutritionnelles</h3>
              <div className="nutrition-inputs">
                <div className="nutri-input">
                  <Flame size={20} />
                  <input type="number" value={nutritionData.calories} onChange={e => setNutritionData({...nutritionData, calories: parseInt(e.target.value) || 0})} />
                  <span>kcal</span>
                </div>
                <div className="nutri-input">
                  <Beef size={20} />
                  <input type="number" value={nutritionData.protein} onChange={e => setNutritionData({...nutritionData, protein: parseInt(e.target.value) || 0})} />
                  <span>g prot.</span>
                </div>
                <div className="nutri-input">
                  <Wheat size={20} />
                  <input type="number" value={nutritionData.carbs} onChange={e => setNutritionData({...nutritionData, carbs: parseInt(e.target.value) || 0})} />
                  <span>g gluc.</span>
                </div>
                <div className="nutri-input">
                  <Droplets size={20} />
                  <input type="number" value={nutritionData.fat} onChange={e => setNutritionData({...nutritionData, fat: parseInt(e.target.value) || 0})} />
                  <span>g lip.</span>
                </div>
              </div>
              <p className="nutri-note">Tu peux ajuster les valeurs si besoin</p>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="secondary-btn">Annuler</button>
          <button onClick={handleSave} disabled={!nutritionData || !description.trim() || isSaving} className="primary-btn">
            {isSaving ? <Loader size={18} className="spin" /> : <Plus size={18} />}
            Ajouter
          </button>
        </div>
      </div>
    </div>
  );
};

export default JournalPage;
